import React, { useEffect, useMemo, useState } from 'react';
import { PublicLayout } from '@/components/PublicLayout';
import { MenuItemCard } from '@/components/MenuItemCard';
import { api } from '@/lib/api';
import { Search, ChevronRight, Utensils } from 'lucide-react';
import { TID } from '@/lib/testIds';
import { useSettings } from '@/contexts/SettingsContext';

// Delivery brand marks (kept intentionally minimal — text over white pill).
const UberEatsMark = () => (
  <span className="font-bold tracking-tight" style={{ letterSpacing: '-0.02em' }}>
    Uber <span className="text-[hsl(var(--sb-green))]">Eats</span>
  </span>
);
const DeliverooMark = () => (
  <span className="font-bold tracking-tight" style={{ color: '#00CCBC' }}>deliveroo</span>
);
const JustEatMark = () => (
  <span className="font-bold tracking-tight" style={{ color: '#FF8000' }}>Just Eat</span>
);

export default function MenuPage() {
  const { settings } = useSettings();
  const [menu, setMenu] = useState([]);
  // 'all' shows every category grouped by heading. Category id filters to one.
  const [activeCat, setActiveCat] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/menu/full').then(({ data }) => {
      setMenu(data || []);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!menu.length) return [];
    const q = query.trim().toLowerCase();
    return menu
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (i) => !q || i.name.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [menu, query]);

  // Precedence: active search → filtered results across all categories.
  //             else 'all'    → every category with items (grouped headings preserved).
  //             else category → just that one category.
  const display = query
    ? filtered
    : activeCat === 'all'
      ? menu
      : menu.filter((c) => c.id === activeCat);

  const social = settings?.social_links || {};
  const deliveryPartners = [
    { key: 'uber_eats', label: 'Uber Eats', href: social.uber_eats, Mark: UberEatsMark },
    { key: 'deliveroo', label: 'Deliveroo', href: social.deliveroo, Mark: DeliverooMark },
    { key: 'just_eat', label: 'Just Eat', href: social.just_eat, Mark: JustEatMark },
  ].filter((p) => !!p.href);

  return (
    <PublicLayout>
      <div data-testid={TID.menu.page} className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest">Our Menu</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white">AUTHENTIC FLAVOURS</h1>
            <p className="text-white/70 mt-1 text-sm">All meals come with plain rice / rice &amp; peas &amp; salad. Halal certified.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid={TID.menu.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search dishes"
              className="w-full h-11 pl-9 pr-3 bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] rounded-md text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]"
            />
          </div>
        </div>

        {/* Compact external delivery strip — only shown when a genuine URL is configured per platform */}
        {deliveryPartners.length > 0 && (
          <div
            data-testid="menu-delivery-strip"
            className="mb-6 sb-card px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] grid place-items-center">
                <Utensils size={14} />
              </div>
              <div className="text-sm text-white/85">
                <span className="text-[hsl(var(--sb-gold))] uppercase text-[10px] tracking-widest block leading-none mb-0.5">Order for delivery</span>
                Grab Smithy Browns on your favourite platform
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {deliveryPartners.map(({ key, label, href, Mark }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Order on ${label}`}
                  data-testid={`menu-delivery-${key}`}
                  className="inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-white text-black hover:bg-white/90 transition-colors border border-white/10 text-sm active:scale-[0.98]"
                >
                  <Mark />
                  <ChevronRight size={13} />
                </a>
              ))}
            </div>
          </div>
        )}

        {!query && (
          <div
            data-testid={TID.menu.tabs}
            className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-1 px-1 sticky top-16 bg-[hsl(var(--sb-bg))]/90 backdrop-blur z-20"
          >
            <button
              key="all"
              onClick={() => setActiveCat('all')}
              data-testid="menu-tab-all"
              className={`whitespace-nowrap h-10 px-4 rounded-full text-sm border transition-colors ${
                activeCat === 'all'
                  ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]'
                  : 'bg-[hsl(var(--sb-surface))] text-white/80 border-[hsl(var(--sb-border))] hover:border-white/30'
              }`}
            >
              All
            </button>
            {menu.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`whitespace-nowrap h-10 px-4 rounded-full text-sm border transition-colors ${
                  activeCat === c.id
                    ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]'
                    : 'bg-[hsl(var(--sb-surface))] text-white/80 border-[hsl(var(--sb-border))] hover:border-white/30'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 space-y-10">
          {display.map((cat) => (
            <section key={cat.id}>
              <div className="flex items-baseline gap-3 mb-3">
                <h2 className="font-display text-3xl text-white">{cat.name.toUpperCase()}</h2>
                {cat.description && <span className="text-xs text-white/55">{cat.description}</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {cat.items.map((item) => (
                  <MenuItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ))}
          {!display.length && (
            <div className="text-center text-white/60 py-12">No dishes match your search.</div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
